// frontend/src/components/EmpresaPortal/NotificacionesBell.js
// Campana de notificaciones del portal de empresa: muestra las 5 más
// recientes (propias de la empresa + globales) con su estado de lectura.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell } from 'lucide-react';
import './NotificacionesBell.css';

const NotificacionesBell = () => {
  const [abierto, setAbierto] = useState(false);
  const [notificaciones, setNotificaciones] = useState([]);
  const [noLeidas, setNoLeidas] = useState(0);
  const [cargando, setCargando] = useState(false);
  const contenedorRef = useRef(null);

  const cargarConteo = useCallback(async () => {
    try {
      const res = await fetch('/api/notificaciones/mias/conteo-no-leidas', { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      setNoLeidas(data.conteo || 0);
    } catch (e) { /* silencioso: no bloquea el resto del portal */ }
  }, []);

  useEffect(() => {
    cargarConteo();
  }, [cargarConteo]);

  useEffect(() => {
    const handleClickFuera = (e) => {
      if (contenedorRef.current && !contenedorRef.current.contains(e.target)) {
        setAbierto(false);
      }
    };
    document.addEventListener('mousedown', handleClickFuera);
    return () => document.removeEventListener('mousedown', handleClickFuera);
  }, []);

  const cargarNotificaciones = async () => {
    setCargando(true);
    try {
      const res = await fetch('/api/notificaciones/mias', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setNotificaciones(Array.isArray(data) ? data : []);
      }
    } catch (e) { /* ignorar */ } finally {
      setCargando(false);
    }
  };

  const toggleAbierto = () => {
    const next = !abierto;
    setAbierto(next);
    if (next) cargarNotificaciones();
  };

  const marcarLeida = async (id) => {
    setNotificaciones(prev => prev.map(n => (n.id === id ? { ...n, leida: true } : n)));
    setNoLeidas(prev => Math.max(0, prev - 1));
    try {
      await fetch(`/api/notificaciones/${id}/marcar-leida`, { method: 'POST', credentials: 'include' });
    } catch (e) { /* el conteo real se recalcula en el próximo cargarConteo */ }
  };

  const marcarTodasLeidas = async () => {
    setNotificaciones(prev => prev.map(n => ({ ...n, leida: true })));
    setNoLeidas(0);
    try {
      await fetch('/api/notificaciones/marcar-todas-leidas', { method: 'POST', credentials: 'include' });
    } catch (e) { /* ignorar */ }
  };

  const formatearFecha = (fecha) => new Date(fecha).toLocaleString('es-MX', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
  });

  return (
    <div className="nb-container" ref={contenedorRef}>
      <button type="button" className="nb-bell" onClick={toggleAbierto} title="Notificaciones">
        <Bell size={18} />
        {noLeidas > 0 && <span className="nb-badge">{noLeidas > 9 ? '9+' : noLeidas}</span>}
      </button>

      {abierto && (
        <div className="nb-panel">
          <div className="nb-panel-header">
            <strong>Notificaciones</strong>
            {noLeidas > 0 && (
              <button type="button" className="nb-marcar-todas" onClick={marcarTodasLeidas}>
                Marcar todas como leídas
              </button>
            )}
          </div>

          {cargando ? (
            <p className="nb-vacio">Cargando...</p>
          ) : notificaciones.length === 0 ? (
            <p className="nb-vacio">No tienes notificaciones.</p>
          ) : (
            <div className="nb-lista">
              {notificaciones.map(notif => (
                <div
                  key={notif.id}
                  className={`nb-item ${notif.leida ? '' : 'nb-item-no-leida'}`}
                  onClick={() => !notif.leida && marcarLeida(notif.id)}
                >
                  <div className="nb-item-header">
                    <span className="nb-item-titulo">{notif.titulo}</span>
                    {!notif.leida && <span className="nb-dot" title="No leída" />}
                  </div>
                  <p className="nb-item-mensaje">{notif.mensaje}</p>
                  <span className="nb-item-fecha">{formatearFecha(notif.created_at)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificacionesBell;
